import type { AccountNumberState, ClientUploadForClient, InfoRequestForClient, WithdrawnInfoRequestForClient } from '@steward-app/steward-types';
import { createApiClient } from './client';

/**
 * The client's onboarding form, through the façade's `/me/onboarding-forms/{firmWorkspaceId}`
 * relays (crossmarc/steward-app#1765). Workspace-less: an onboarding client has no workspace.
 * A `403 STEP_UP_REQUIRED` from any of these is handled by the API client's interceptor
 * (`runStepUpCeremony`), which runs the ceremony and retries, so no caller here handles it.
 */

export interface OnboardingIdentityView {
  subject: 'client' | 'spouse';
  /** The last four digits. Never the number. */
  taxIdHint: string;
  updatedAt: string;
  /** Who entered the current number (docs/43 §8.4, slice 9): `advisor` when firm staff did. Absent
   *  from an older server. Never names the advisor. */
  enteredBy?: 'client' | 'advisor';
  /** When the advisor's entry replaced a number the client had entered, while it is current. */
  replacedClientValueAt?: string;
}

export interface OnboardingDetailsView {
  attributes: Record<string, unknown>;
  version: number;
  updatedAt: string;
}

export interface OnboardingFormView {
  firmName: string;
  /** The firm's slug, which addresses the onboarding record's routes (`onboardingRecord.ts`,
   *  docs/43 slice 4c). Absent from an older façade: the form then cannot load its answers. */
  firmSlug?: string;
  /** The firm's brand key (steward-app#2770), which names the firm the way the snapshot form does
   *  (`intakeBrand`). Absent from an older façade: the form then names the firm by `firmName`. */
  brandKey?: string;
  details: OnboardingDetailsView | null;
  prefill: Record<string, unknown> | null;
  identities: OnboardingIdentityView[];
  /** True once this firm has set up Steward for the caller (crossmarc/steward-app#1856 bullet
   *  5); the form is read-only from the moment this loads, not only once a write is refused. */
  formClosed: boolean;
  /** Who closed it (crossmarc/steward-app#2550): `'firm'` when the firm pressed Close form,
   *  `'setup'` when Steward was set up. Absent from an older façade, or when the façade could
   *  not tell; the form then shows the set-up wording, as before. */
  formClosedBy?: 'firm' | 'setup';
  /** The invited (login) address, shown read-only (steward-app#1883). Absent if the server could
   *  not read it; the save route stamps it over `client_email` either way. */
  invitedEmail?: string;
  /** The optional sections the firm asked for (steward-app#1882): any of `spouse`,
   *  `trustedContact`, `beneficiaries`, `aboutYou`. Absent means every section is shown. */
  askedSections?: string[];
  /** The firm's sent, not-withdrawn requests for this client, each with the files they have
   *  added (steward-app#2284). Absent when there are none. */
  requests?: InfoRequestForClient[];
  /** The latest send's note, as text. Present only alongside `requests`. */
  requestsNote?: string;
  /** Requests the firm withdrew that hold a file or answer of the client's own (steward-app#2302): what was
   *  asked, nothing else. Absent when there are none. Never overlaps `requests`. */
  withdrawnRequests?: WithdrawnInfoRequestForClient[];
  /** For each account-number request in `requests`, whether a number is saved and its last
   *  characters (docs/38 §4.2, steward-app#2298). Hints only, never a number. Absent when no
   *  request asks for one. */
  accountNumbers?: AccountNumberState[];
}

const base = (firmWorkspaceId: string) => `/me/onboarding-forms/${encodeURIComponent(firmWorkspaceId)}`;

export const onboardingFormQueryKey = (firmWorkspaceId: string) => ['onboarding-form', firmWorkspaceId] as const;

export function getOnboardingForm(token: string, firmWorkspaceId: string): Promise<OnboardingFormView> {
  return createApiClient(token).get<OnboardingFormView>(base(firmWorkspaceId));
}

export function saveOnboardingSection(
  token: string, firmWorkspaceId: string, section: string, values: Record<string, string | boolean | string[]>,
): Promise<OnboardingDetailsView> {
  return createApiClient(token).post<OnboardingDetailsView>(`${base(firmWorkspaceId)}/sections/${encodeURIComponent(section)}`, { values });
}

export function sendOnboardingForm(token: string, firmWorkspaceId: string): Promise<{ sentAt: string }> {
  return createApiClient(token).post<{ sentAt: string }>(`${base(firmWorkspaceId)}/send`, {});
}

export function setOnboardingTaxId(
  token: string, firmWorkspaceId: string, subject: 'client' | 'spouse', taxId: string,
): Promise<OnboardingIdentityView> {
  return createApiClient(token).post<OnboardingIdentityView>(`${base(firmWorkspaceId)}/identities/${subject}/tax-id`, { taxId });
}

export function clearOnboardingTaxId(token: string, firmWorkspaceId: string, subject: 'client' | 'spouse'): Promise<void> {
  return createApiClient(token).delete<void>(`${base(firmWorkspaceId)}/identities/${subject}/tax-id`);
}

// ── Files under an advisor's request (steward-app#2284) ───────────────────────────
// A mint, a direct PUT to the returned `uploadUrl`, then a commit. The façade refuses a request
// that is not open (404 `INFO_REQUEST_NOT_FOUND`) and a full one (409 `INFO_REQUEST_UPLOAD_LIMIT`).

export const INFO_REQUEST_NOT_FOUND = 'INFO_REQUEST_NOT_FOUND';
export const INFO_REQUEST_UPLOAD_LIMIT = 'INFO_REQUEST_UPLOAD_LIMIT';
/** appcore's 400 when a stored file's first bytes are not the declared type's. The façade's
 *  `errorToResponse` forwards appcore's own code, so it reaches the browser under this name. */
export const ONBOARDING_DOCUMENT_CONTENT_MISMATCH = 'ONBOARDING_DOCUMENT_CONTENT_MISMATCH';

const requestBase = (firmWorkspaceId: string, requestId: string) => `${base(firmWorkspaceId)}/requests/${encodeURIComponent(requestId)}/documents`;

export function mintInfoRequestUpload(
  token: string, firmWorkspaceId: string, requestId: string, body: { filename: string; mimeType: string; sizeBytes: number },
): Promise<{ uploadUrl: string; entityId: string }> {
  return createApiClient(token).post<{ uploadUrl: string; entityId: string }>(`${requestBase(firmWorkspaceId, requestId)}/upload-url`, body);
}

export function commitInfoRequestUpload(
  token: string, firmWorkspaceId: string, requestId: string, body: { entityId: string; filename: string; mimeType: string },
): Promise<ClientUploadForClient> {
  return createApiClient(token).post<ClientUploadForClient>(requestBase(firmWorkspaceId, requestId), body);
}

export function deleteInfoRequestUpload(token: string, firmWorkspaceId: string, requestId: string, documentId: string): Promise<void> {
  return createApiClient(token).delete<void>(`${requestBase(firmWorkspaceId, requestId)}/${encodeURIComponent(documentId)}`);
}

// ── A full account number under an advisor's request (docs/38 §4.2, steward-app#2298) ──
// Its own route, apart from the section saves. The body is the only place the number travels; the
// answer is the masked state of the slot (a hint, never the number). A 404 means the request is no
// longer open. The step-up gate (reveal tier) is run by the API client's interceptor.

const numberPath = (firmWorkspaceId: string, requestId: string) => `${base(firmWorkspaceId)}/account-numbers/${encodeURIComponent(requestId)}`;

export function setOnboardingAccountNumber(
  token: string, firmWorkspaceId: string, requestId: string, body: { account: string; routing?: string },
): Promise<AccountNumberState> {
  return createApiClient(token).post<AccountNumberState>(numberPath(firmWorkspaceId, requestId), body);
}

export function clearOnboardingAccountNumber(token: string, firmWorkspaceId: string, requestId: string): Promise<void> {
  return createApiClient(token).delete<void>(numberPath(firmWorkspaceId, requestId));
}
